import { gql } from '@apollo/client';
import type { PageableContent, SafetyOccurrence, SafetyReview, SafetyReviewHistory, SafetyReviewRequest } from '@cobliteam/common-types';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import type { Configuration } from '../..';
import type { QuerySafetyReviewHistoryArgs, SafetyReviewHistoryPage } from '../../__generated__/graphql';
import graphQLRequester from '../../request/GraphQLRequester';
import HttpMethod from '../../request/HttpMethod';
import Requester from '../../request/Requester';
import type { IRequest } from '../../request/Types';

const querySafetyReviewHistory = gql`
    query safetyReviewHistory(
      $mediaId: ID!
      $page: Int
      $size: Int
      $sort: [String]
    ) {
      safetyReviewHistory(
        mediaId: $mediaId
        page: $page
        size: $size
        sort: $sort
      ) {
        total_elements
        first
        last
        empty
        number
        number_of_elements
        size
        total_pages
        content {
          id
          user_id
          user {
            full_name
          }
          created_at
          snapshot {
            status
            driver_id
            driver {
              name
            }
            occurrences {
              id
              name
            }
            severity
          }
        }
      }
    }
  `;

export class CameraSafetyRequester {
  private requester: Requester;

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
  }

  public getCameraSafetyOccurrences = async (): Promise<SafetyOccurrence[]> => {
    const requestData: IRequest = {
      endpoint: '/cameras/safety-occurrences',
      method: HttpMethod.GET,
    };

    const { data } = await this.requester.request(requestData);

    return camelcaseKeys(data);
  };

  public upsertCameraSafetyReview = async (safetyReviewRequest: SafetyReviewRequest): Promise<SafetyReview> => {
    const requestData: IRequest = {
      endpoint: '/cameras/safety-review',
      method: HttpMethod.PUT,
      body: JSON.stringify(snakecaseKeys(safetyReviewRequest)),
    };

    const { data } = await this.requester.request(requestData);

    return camelcaseKeys(data);
  };

  public getSafetyReviewHistory = async (args: QuerySafetyReviewHistoryArgs): Promise<PageableContent<SafetyReviewHistory>> => {
    const { safetyReviewHistory } = await graphQLRequester.query<{ safetyReviewHistory: SafetyReviewHistoryPage }>({
      query: querySafetyReviewHistory,
      variables: args,
    });

    return camelcaseKeys(safetyReviewHistory as any, { deep: true });
  };
}

export default CameraSafetyRequester;
